import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Redirect,
  Switch,
  BrowserRouter as Router,
  withRouter,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { isAgencyDoctor, isPatient } from 'utils/userTypeHelper';

import AccessRoute from 'components/atoms/routes/accessRoute';
import common from 'constants/common';
import path from 'constants/paths';
import ConnectModal from 'components/organisms/connect';
// import SurveyButton from 'components/atoms/surveyButton';
import Layout from 'components/templates/layout';
import PatientPage from 'pages/rpm/patients/patient';
import DashboardPage from 'pages/rpm/dashboard';
import PatientDetails from 'pages/rpm/patients/patientDetails';
import ActivityReport from 'pages/rpm/practice/activityReport';
import InterActiveReport from 'pages/rpm/practice/interactiveReport';
import AdherenceReport from 'pages/rpm/practice/adherenceReport';
import AdherenceLast30DaysReport from 'pages/rpm/practice/adherenceReport/last30DaysAdherence';
// import ServicePlans from 'pages/servicePlans';
import ComplianceReport from 'pages/rpm/practice/complianceReport';
import ClinicalMetrics from 'pages/rpm/practice/clinicalMetrics';
import OnboardingStatus from 'pages/rpm/practice/onboardingStatus';
import MedicareBilling from 'pages/rpm/practice/medicareBilling';
// import PracticeOverview from 'pages/rpm/practice/practiceOverview';
import Profile from 'pages/profile';
import AdminPatients from 'pages/admin/patients';
import AdminPatientAdd from 'pages/admin/patientAdd';
import AdminPatient from 'pages/admin/patient';
import AdminPatientEdit from 'pages/admin/patientEdit';
import AdminUsers from 'pages/admin/users';
import AdminUserAdd from 'pages/admin/userAdd';
import AdminUser from 'pages/admin/user';
import AdminAgencyDoctorList from 'pages/admin/hhaDoctors';
import AdminAgencyDoctorInfo from 'pages/admin/hhaDoctor';
import AdminAgencyDoctorAdd from 'pages/admin/hhaDoctorAdd';
import AdminAgencyDoctorEdit from 'pages/admin/hhaDoctorEdit';
import AdminUserEdit from 'pages/admin/userEdit';
import AdminClinics from 'pages/admin/clinics';
import AdminClinic from 'pages/admin/clinic';
import AdminClinicEdit from 'pages/admin/clinicEdit';
import AdminClinicAdd from 'pages/admin/clinicAdd';
import AdminAgencies from 'pages/admin/agencies';
import AdminAgency from 'pages/admin/agency';
import AdminAgencyEdit from 'pages/admin/agencyEdit';
import AdminAgencyAdd from 'pages/admin/agencyAdd';
import CCMPatientsActivities from 'pages/ccm/patientsActivities';
import CCMBillingReport from 'pages/ccm/practice/billingReport';
import CCMBillingReportV2 from 'pages/ccm/practice/billingReportV2';
import CCMInteractiveTimeReport from 'pages/ccm/practice/ccmInteractiveReport';
import CCMPatientDetails from 'pages/ccm/patientDetails';
import CCMConsentForm from 'pages/ccm/consentForm';
import TermsPage from 'pages/termsOfService';
import NoActiveClinicPage from 'components/atoms/noActiveClinic';
import PatientsStatusReport from 'pages/rpm/practice/patientsStatusReport';
import StatisticsReport from 'pages/rpm/practice/statisticsReport';
import PerformanceReview from 'pages/rpm/practice/performanceReview';
import InteractionStatus from 'pages/rpm/practice/interactionStatus';
// import AdminBulkUpload from 'pages/adminBulkUpload';
import WS, {
  NEW_READING_EVENT,
  REPORT_READY_EVENT,
  ERROR,
  NEW_RESERVED_READING_EVENT,
  CLEAR_RESERVED_READINGS_EVENT,
} from 'services/wsService';
import {
  REFRESH_MESSAGE,
  successMessages,
  errorMessages,
} from 'constants/messages';
import useUser from 'utils/useUser';
import { useAuth0 } from '@auth0/auth0-react';
import Loader from 'react-loader-spinner';
import NotFound from 'components/atoms/notFound';
import {
  PLATFORM_MODE_CCM,
  PLATFORM_MODE_PATIENT_ACCEPTANCE,
} from 'utils/accessManager/constants';

const MainApp = () => {
  const [wsClient, setWsClient] = useState(null);
  const [connected, setConnected] = useState(false);
  const { logout, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    window.getAccessTokenSilently = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  const {
    isLoading,
    userType,
    guid,
    logoutFlag,
    platformMode,
    termsOfService,
    isBlocked,
  } = useUser();

  const {
    user: { setDevice, getProfile },
    connect: { passWsClient },
    doctor: { updateActivities, clearActivities },
  } = useDispatch();

  const refreshPage = () => {
    window.location.reload();
  };

  useEffect(async () => {
    const init = async () => {
      await WS.connect();
      setWsClient(WS);
      setConnected(true);
      passWsClient(WS);
      if (!userType) await getProfile();
      setDevice();
    };

    if (!userType) init();

    if (userType) {
      WS.on(NEW_READING_EVENT, () => {
        toast.info(<div onClick={refreshPage}>{REFRESH_MESSAGE}</div>, {
          ...common.toastSettings,
          autoClose: false,
          toastId: 'refreshToast',
        });
      });
      WS.on(REPORT_READY_EVENT, (data) => {
        toast.success(
          successMessages.reportReady(data.reportType || ''),
          common.toastSettings,
        );
      });
      WS.on(ERROR, (data) => {
        toast.error(
          errorMessages.report(data.reportType, data.message),
          common.toastSettings,
        );
      });
      if (isAgencyDoctor(userType)) {
        WS.on(NEW_RESERVED_READING_EVENT, (data) => {
          console.log('NEW_RESERVED_READING_EVENT', data);
          updateActivities(data);
        });
        WS.on(CLEAR_RESERVED_READINGS_EVENT, (data) => {
          console.log('CLEAR_RESERVED_READINGS_EVENT', data);
          clearActivities(data);
        });
      }
    }
  }, [userType]);

  if (logoutFlag) {
    return logout({ returnTo: window.location.origin });
  }

  if (isBlocked) {
    return (
      <Layout disableNavigation>
        <NoActiveClinicPage />
      </Layout>
    );
  }

  if (isLoading || !userType || !guid)
    return (
      <Loader className="loader fixed" type="Oval" color={common.loaderColor} />
    );

  const GetDashboard = () => {
    if (isPatient(userType)) return <Redirect to={`/patient/${guid}`} />;
    if (platformMode === PLATFORM_MODE_CCM) return <Redirect to={path.ccm} />;
    if (platformMode === PLATFORM_MODE_PATIENT_ACCEPTANCE)
      return <Redirect to={path.adminPatients} />;
    return <DashboardPage wsClient={wsClient} wsConnected={connected} />;
  };

  if (!termsOfService) return <TermsPage />;
  return (
    <>
      <Router>
        <Layout>
          <Switch>
            <AccessRoute exact path={path.dashboard}>
              {GetDashboard()}
            </AccessRoute>
            <AccessRoute
              exact
              path={path.patientDetails}
              component={PatientDetails}
            />
            <AccessRoute
              exact
              path={path.medicareBilling}
              component={MedicareBilling}
            />
            <AccessRoute
              exact
              path={path.patientGuid}
              component={PatientPage}
            />
            <AccessRoute
              exact
              path={path.activityReport}
              component={ActivityReport}
            />
            <AccessRoute
              exact
              path={path.interactiveReport}
              component={InterActiveReport}
            />
            <AccessRoute
              exact
              path={path.adherenceReport}
              component={AdherenceReport}
            />
            <AccessRoute
              exact
              path={path.adherenceReportLast30Days}
              component={AdherenceLast30DaysReport}
            />
            {/* <AccessRoute
              exact
              path={path.servicePlans}
              component={ServicePlans}
            /> */}
            <AccessRoute
              exact
              path={path.onboardingStatus}
              component={OnboardingStatus}
            />
            <AccessRoute
              exact
              path={path.complianceReport}
              component={ComplianceReport}
            />
            <AccessRoute
              exact
              path={path.clinicalMetrics}
              component={ClinicalMetrics}
            />
            {/* <AccessRoute
              exact
              path={path.practiceOverview}
              component={PracticeOverview}
            /> */}
            <AccessRoute
              exact
              path={path.adminPatients}
              component={AdminPatients}
            />
            <AccessRoute
              exact
              path={path.adminPatientAdd}
              component={AdminPatientAdd}
            />
            <AccessRoute
              exact
              path={path.adminPatient}
              component={AdminPatient}
            />
            <AccessRoute
              exact
              path={path.adminPatientEdit}
              component={AdminPatientEdit}
            />
            {/* <AccessRoute
              exact
              path={path.adminBulkUpload}
              component={AdminBulkUpload}
            /> */}

            <AccessRoute exact path={path.adminUsers} component={AdminUsers} />
            <AccessRoute exact path={path.adminUser} component={AdminUser} />
            <AccessRoute
              exact
              path={path.adminAgencyDoctorList}
              component={AdminAgencyDoctorList}
            />
            <AccessRoute
              exact
              path={path.adminAgencyDoctor}
              component={AdminAgencyDoctorInfo}
            />
            <AccessRoute
              exact
              path={path.adminAgencyDoctorAdd}
              component={AdminAgencyDoctorAdd}
            />
            <AccessRoute
              exact
              path={path.adminAgencyDoctorEdit}
              component={AdminAgencyDoctorEdit}
            />
            <AccessRoute
              exact
              path={path.adminUserEdit}
              component={AdminUserEdit}
            />
            <AccessRoute
              exact
              path={path.adminUserAdd}
              component={AdminUserAdd}
            />
            <AccessRoute
              exact
              path={path.adminClinics}
              component={AdminClinics}
            />
            <AccessRoute
              exact
              path={path.adminClinic}
              component={AdminClinic}
            />
            <AccessRoute
              exact
              path={path.adminClinicEdit}
              component={AdminClinicEdit}
            />
            <AccessRoute
              exact
              path={path.adminClinicAdd}
              component={AdminClinicAdd}
            />
            <AccessRoute
              exact
              path={path.adminAgencies}
              component={AdminAgencies}
            />
            <AccessRoute
              exact
              path={path.adminAgency}
              component={AdminAgency}
            />
            <AccessRoute
              exact
              path={path.adminAgencyEdit}
              component={AdminAgencyEdit}
            />
            <AccessRoute
              exact
              path={path.adminAgencyAdd}
              component={AdminAgencyAdd}
            />
            <AccessRoute
              exact
              path={path.ccmPatient}
              component={CCMPatientDetails}
            />
            <AccessRoute
              exact
              path={path.ccm}
              component={CCMPatientsActivities}
            />
            <AccessRoute
              exact
              path={path.ccmBillingReport}
              component={CCMBillingReport}
            />
            <AccessRoute
              exact
              path={path.ccmBillingReportV2}
              component={CCMBillingReportV2}
            />
            <AccessRoute
              exact
              path={path.ccmInteractiveTimeReport}
              component={CCMInteractiveTimeReport}
            />
            <AccessRoute
              exact
              path={path.ccmConsent}
              component={CCMConsentForm}
            />
            <AccessRoute
              exact
              path={path.patientsStatusReport}
              component={PatientsStatusReport}
            />
            <AccessRoute
              exact
              path={path.statisticsReport}
              component={StatisticsReport}
            />
            <AccessRoute
              exact
              path={path.performanceReview}
              component={PerformanceReview}
            />
            <AccessRoute
              exact
              path={path.interactionStatus}
              component={InteractionStatus}
            />
            <AccessRoute exact path={path.profile} component={Profile} />
            <AccessRoute exact path={path.notFound} component={NotFound} />
            <Redirect to={path.notFound} />
          </Switch>
        </Layout>
      </Router>
      <ConnectModal />
      {/* <SurveyButton /> */}
    </>
  );
};

export default withRouter(MainApp);
